// Web Push registration helper. Frontend side.
//
// Flow:
//   1. Register /sw.js
//   2. Ask Notification.requestPermission()
//   3. Get VAPID public key from /push/public-key
//   4. swRegistration.pushManager.subscribe({applicationServerKey})
//   5. POST subscription to /push/subscribe
//
// The SW needs the auth token to call /notifications on push. It can't read
// localStorage, so we mirror the token into IndexedDB on login/logout.

import * as api from './api';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';

// ---- Token mirror (localStorage → IndexedDB for SW access) ----------------

function openIdb(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const req = indexedDB.open('vava', 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('auth')) db.createObjectStore('auth');
    };
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

export async function mirrorTokenToIdb(token: string | null): Promise<void> {
  try {
    const db = await openIdb();
    const tx = db.transaction('auth', 'readwrite');
    const store = tx.objectStore('auth');
    if (token) store.put(token, 'token'); else store.delete('token');
    await new Promise((res, rej) => {
      tx.oncomplete = () => res(null);
      tx.onerror = () => rej(tx.error);
    });
  } catch {
    // IndexedDB unavailable (private mode? quota?) — SW push fallback fetch
    // won't work, but explicit-payload pushes would still display generic msg.
  }
}

// ---- Service worker + subscription ----------------------------------------

export function pushSupported(): boolean {
  return typeof navigator !== 'undefined' &&
         'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window;
}

export function pushPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch (e) {
    console.warn('SW register failed:', (e as Error).message);
    return null;
  }
}

// base64url → Uint8Array for applicationServerKey
function b64uToBytes(b64u: string): Uint8Array {
  const pad = '='.repeat((4 - b64u.length % 4) % 4);
  const b64 = (b64u + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Uint8Array → base64url (for sending subscription keys to server)
function bytesToB64u(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

/** Returns the current push subscription (if any) without prompting. */
export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Asks for permission, subscribes via VAPID public key, POSTs to /push/subscribe.
 *  Returns true on success. */
export async function subscribeToPush(): Promise<boolean> {
  if (!pushSupported()) throw new Error('此浏览器不支持推送通知');
  // 1. Permission
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('用户拒绝了通知权限');
  // 2. SW + public key
  const reg = await navigator.serviceWorker.ready;
  const res = await fetch(`${API_BASE}/push/public-key`);
  if (!res.ok) throw new Error('无法获取 VAPID 公钥');
  const { publicKey } = await res.json();
  if (!publicKey) throw new Error('VAPID 公钥未配置');
  // 3. Subscribe
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64uToBytes(publicKey).buffer as ArrayBuffer
    });
  }
  // 4. Send to server
  const json = sub.toJSON();
  const payload = {
    endpoint: json.endpoint!,
    keys: {
      p256dh: json.keys?.p256dh ?? bytesToB64u(sub.getKey('p256dh')),
      auth: json.keys?.auth ?? bytesToB64u(sub.getKey('auth'))
    }
  };
  const token = localStorage.getItem('vava_token');
  const postRes = await fetch(`${API_BASE}/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  if (!postRes.ok) {
    const e = await postRes.json().catch(() => ({}));
    throw new Error(e.error || `订阅失败 (${postRes.status})`);
  }
  return true;
}

/** Unsubscribes locally + tells server. Best-effort. */
export async function unsubscribeFromPush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ok */ }
  const token = localStorage.getItem('vava_token');
  try {
    await fetch(`${API_BASE}/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ endpoint })
    });
  } catch { /* ok */ }
}
